export const PROTOCOL_VERSION = 1;

export const EVENTS = Object.freeze({
  QUICK_PLAY: "queue:quick-play",
  LEAVE_ROOM: "room:leave",
  PLAYER_SNAPSHOT: "player:snapshot",
  PLAYER_DEATH: "player:death",
  CHECKPOINT_CLAIM: "player:checkpoint",
  PICKUP_CLAIM: "player:pickup",
  FINISH_CLAIM: "player:finish",
  REMATCH_VOTE: "match:rematch",

  ROOM_JOINED: "room:joined",
  ROOM_STATE: "room:state",
  MATCH_STATE: "match:state",
  COURSE: "match:course",
  REMOTE_SNAPSHOT: "player:remote-snapshot",
  PLAYER_LEFT: "player:left",
  ACTION_REJECTED: "action:rejected",
  SERVER_ERROR: "server:error"
});

export const MATCH_PHASES = Object.freeze({
  LOBBY: "lobby",
  COUNTDOWN: "countdown",
  ACTIVE: "active",
  FINISHED: "finished"
});

export const PLATFORM_TYPES = Object.freeze({
  NORMAL: "normal",
  MOVING: "moving",
  BOUNCE: "bounce",
  PICKUP: "pickup",
  SHELTER: "shelter",
  FINISH: "finish"
});

export const LIMITS = Object.freeze({
  MIN_PLAYERS: 2,
  MAX_PLAYERS: 4,
  SNAPSHOTS_PER_SECOND: 20,
  MAX_NAME_LENGTH: 24,
  MAX_POSITION: 50_000,
  MAX_SPEED: 2_500,
  MAX_SEQUENCE: Number.MAX_SAFE_INTEGER
});

export const PLAYER_COLORS = Object.freeze([
  Object.freeze({ id: "coral", hex: "#ff7b35", label: "Coral" }),
  Object.freeze({ id: "blue", hex: "#6f8ff2", label: "Blue" }),
  Object.freeze({ id: "pink", hex: "#ef65a6", label: "Pink" }),
  Object.freeze({ id: "mint", hex: "#59c98b", label: "Mint" })
]);

export function normalizePlayerColor(value) {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  return PLAYER_COLORS.find((color) => color.id === key || color.hex === key) ?? null;
}

const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);
const isIntegerIn = (value, min, max) =>
  Number.isSafeInteger(value) && value >= min && value <= max;

function result(ok, value, errors = []) {
  return ok ? { ok: true, value } : { ok: false, errors };
}

export function validateQuickPlay(payload) {
  if (payload === undefined || payload === null) {
    return result(true, { name: "Player", color: PLAYER_COLORS[0].id });
  }
  if (!isObject(payload)) return result(false, null, ["payload must be an object"]);

  const name = typeof payload.name === "string" ? payload.name.trim() : "Player";
  if (name.length < 1 || name.length > LIMITS.MAX_NAME_LENGTH) {
    return result(false, null, [
      `name must contain 1-${LIMITS.MAX_NAME_LENGTH} characters`
    ]);
  }

  let color = PLAYER_COLORS[0].id;
  if (payload.color !== undefined && payload.color !== null && payload.color !== "") {
    const chosen = normalizePlayerColor(payload.color);
    if (!chosen) {
      return result(false, null, ["color must be one of the racer colors"]);
    }
    color = chosen.id;
  }
  return result(true, { name, color });
}

export function validateSnapshot(payload) {
  if (!isObject(payload)) return result(false, null, ["payload must be an object"]);
  const errors = [];
  for (const key of ["x", "y", "vx", "vy"]) {
    if (!isFiniteNumber(payload[key])) errors.push(`${key} must be finite`);
  }
  if (!isIntegerIn(payload.seq, 0, LIMITS.MAX_SEQUENCE)) {
    errors.push("seq must be a non-negative safe integer");
  }
  if (
    payload.clientTime !== undefined &&
    (!isFiniteNumber(payload.clientTime) || payload.clientTime < 0)
  ) {
    errors.push("clientTime must be a non-negative finite number");
  }
  if (isFiniteNumber(payload.x) && Math.abs(payload.x) > LIMITS.MAX_POSITION) {
    errors.push("x is outside the world limit");
  }
  if (isFiniteNumber(payload.y) && Math.abs(payload.y) > LIMITS.MAX_POSITION) {
    errors.push("y is outside the world limit");
  }
  if (isFiniteNumber(payload.vx) && Math.abs(payload.vx) > LIMITS.MAX_SPEED) {
    errors.push("vx exceeds the speed limit");
  }
  if (isFiniteNumber(payload.vy) && Math.abs(payload.vy) > LIMITS.MAX_SPEED) {
    errors.push("vy exceeds the speed limit");
  }
  if (
    payload.facing !== undefined &&
    payload.facing !== -1 &&
    payload.facing !== 1
  ) {
    errors.push("facing must be -1 or 1");
  }
  if (
    payload.animation !== undefined &&
    (typeof payload.animation !== "string" || payload.animation.length > 32)
  ) {
    errors.push("animation must be a string of at most 32 characters");
  }
  if (errors.length) return result(false, null, errors);

  return result(true, {
    x: payload.x,
    y: payload.y,
    vx: payload.vx,
    vy: payload.vy,
    seq: payload.seq,
    clientTime: payload.clientTime ?? 0,
    facing: payload.facing ?? 1,
    animation: payload.animation ?? "idle"
  });
}

export function validateCheckpointClaim(payload) {
  if (!isObject(payload) || !isIntegerIn(payload.index, 0, 99)) {
    return result(false, null, ["index must be an integer between 0 and 99"]);
  }
  return result(true, { index: payload.index });
}

export function validateDeathClaim(payload) {
  if (payload === undefined || payload === null) {
    return result(true, { reason: "water" });
  }
  if (!isObject(payload)) return result(false, null, ["payload must be an object"]);
  const reason = payload.reason ?? "water";
  if (!["water", "fell", "hazard"].includes(reason)) {
    return result(false, null, ["reason must be water, fell, or hazard"]);
  }
  return result(true, { reason });
}

export function validatePickupClaim(payload) {
  if (
    !isObject(payload) ||
    typeof payload.platformId !== "string" ||
    !/^platform-\d{1,3}$/.test(payload.platformId)
  ) {
    return result(false, null, ["platformId must identify a course platform"]);
  }
  return result(true, { platformId: payload.platformId });
}

export function validateEmptyPayload(payload) {
  if (
    payload !== undefined &&
    payload !== null &&
    (!isObject(payload) || Object.keys(payload).length > 0)
  ) {
    return result(false, null, ["payload must be empty"]);
  }
  return result(true, {});
}

export const validators = Object.freeze({
  [EVENTS.QUICK_PLAY]: validateQuickPlay,
  [EVENTS.PLAYER_SNAPSHOT]: validateSnapshot,
  [EVENTS.PLAYER_DEATH]: validateDeathClaim,
  [EVENTS.CHECKPOINT_CLAIM]: validateCheckpointClaim,
  [EVENTS.PICKUP_CLAIM]: validatePickupClaim,
  [EVENTS.FINISH_CLAIM]: validateEmptyPayload,
  [EVENTS.REMATCH_VOTE]: validateEmptyPayload,
  [EVENTS.LEAVE_ROOM]: validateEmptyPayload
});

export function validateClientEvent(event, payload) {
  const validator = validators[event];
  return validator
    ? validator(payload)
    : result(false, null, [`unknown client event: ${String(event)}`]);
}
